import { CalendarDayGroup, CalendarEvent } from 'api/orders/schemas';
import {
  addDays,
  eachDayOfInterval,
  eachWeekOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfToday,
  startOfWeek,
} from 'date-fns';

export enum OrderStatus {
  SUBMITTED = 'Submitted',
  PENDING_GRANITE = 'Pending Granite',
  IN_PROGRESS = 'In progress',
  PENDING_SITE_SURVEY = 'Pending site survey',
  CONSTRUCTION = 'Construction',
  CONFIGURED = 'Configured',
  CONFIRMED = 'Confirmed',
  COMPLETE = 'Complete',
  NEEDS_ATTENTION = 'Needs attention!',
  ON_HOLD = 'On hold',
}

export enum RecentItemStatus {
  CIRCUIT_POSTED = 'Circuit posted',
  INSTALL_CONFIRMED = 'Install confirmed',
  FOC_RECEIVED = 'FOC received',
  ORDER_SHIPPED = 'Order shipped',
  ORDER_CANCELED = 'Order canceled',
  INSTALL_COMPLETED = 'Install completed',
}

export const recentItemStatusOptions = (): { value: string; label: string }[] =>
  Object.values(RecentItemStatus).map((status) => ({
    value: status,
    label: status,
  }));

export function getMonthDays(date: Date): Date[] {
  return eachWeekOfInterval({
    start: startOfMonth(date),
    end: endOfMonth(date),
  }).flatMap((week) =>
    eachDayOfInterval({ start: startOfWeek(week), end: endOfWeek(week) }),
  );
}

const GAP = 160;

export const calendarSlideVariants = {
  enter: (direction: 'left' | 'right') => ({
    x:
      direction === 'right'
        ? `calc(100% + ${GAP}px)`
        : `calc(-100% - ${GAP}px)`,
    opacity: 0,
  }),
  center: {
    x: '0%',
    opacity: 1,
  },
  exit: (direction: 'left' | 'right') => ({
    x:
      direction === 'right'
        ? `calc(-100% - ${GAP}px)`
        : `calc(100% + ${GAP}px)`,
    opacity: 0,
  }),
};

export const eventTypeColors: Record<string, string> = {
  install: '#9FF4A9',
  Cutover: '#8989FF',
  dispatch: '#6C9DFF',
  'Site Survey': '#78CFDA',
};

export const getDateRange = (selectedDate: Date): Date[] => {
  const start = selectedDate ?? startOfToday();
  const end = addDays(start, 13);
  return eachDayOfInterval({ start, end });
};

export const groupEventsByDate = (
  dateRange: Date[],
  days: CalendarDayGroup[],
): Record<string, CalendarEvent[]> => {
  const initialGrouped: Record<string, CalendarEvent[]> = dateRange.reduce(
    (acc, date) => {
      const dateKey = format(date, 'yyyy-MM-dd');
      acc[dateKey] = [];
      return acc;
    },
    {} as Record<string, CalendarEvent[]>,
  );

  for (const day of days) {
    for (const event of day.events) {
      if (event.scheduled_install_date && event.new_value === 'Confirmed') {
        const dateKey = event.scheduled_install_date;
        if (initialGrouped[dateKey]) {
          initialGrouped[dateKey].push(event);
        }
      }
    }
  }

  return initialGrouped;
};

export const getCalendarGridDates = (referenceDate: Date): Date[] => {
  const start = startOfWeek(startOfMonth(referenceDate));
  const end = endOfWeek(endOfMonth(referenceDate));
  return eachDayOfInterval({ start, end });
};
